/**
 * Lending configuration API service (GH #4688)
 *
 * The caller's own institution's lending configuration, served by
 * lending-platform (lending-platform-plugins.openapi.yaml, tag
 * LendingConfiguration):
 *
 *   GET /api/v1/lending-config           lendingConfigGet
 *   PUT /api/v1/lending-config           lendingConfigUpdate (partial merge)
 *   GET /api/v1/lending-config/defaults  lendingConfigGetDefaults
 *
 * The tenant comes from the caller's session. TenantIdHeader is optional in
 * the spec and no request here sends it, so lending-platform resolves the
 * session tenant. The server decides who may write: fi_admin and the
 * platform-administrator roles, every other role refused with 403.
 *
 * Nothing is filled from the defaults (GH #2459): a tenant with no saved
 * configuration answers 404 LENDING_CONFIGURATION_MISSING, and a saved one
 * lacking a field answers 503 LENDING_CONFIGURATION_INCOMPLETE with
 * details.missingFields. Both reach the page as LendingConfigRequestError
 * with the code, so the page can say which state the institution is in.
 *
 * Types derived from lending-platform-plugins.openapi.yaml; no manual
 * interfaces. The spec authors these paths in full, so the client anchors at
 * the origin ('') (the tenant-sla.ts precedent).
 */

import createClient from 'openapi-fetch';
import type {
  components,
  paths as LendingPlatformPaths,
} from '@aaimplatform/institutional-types/lending-platform-plugins';
import { authMiddleware } from '../client';

export type LendingConfiguration = components['schemas']['LendingConfiguration'];
export type LendingConfigurationUpdateRequest =
  components['schemas']['LendingConfigurationUpdateRequest'];
type ErrorResponse = components['schemas']['ErrorResponse'];

const lendingConfigClient = createClient<LendingPlatformPaths>({
  baseUrl: '',
  credentials: 'include',
});
lendingConfigClient.use(authMiddleware);

/**
 * A lending-platform request that did not succeed: the HTTP status and the
 * error code its ErrorResponse body carried, beside the message.
 */
export class LendingConfigRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | undefined,
    /** details.missingFields of a LENDING_CONFIGURATION_INCOMPLETE answer. */
    public readonly missingFields: string[] = [],
  ) {
    super(message);
    this.name = 'LendingConfigRequestError';
  }
}

/** 404 LENDING_CONFIGURATION_MISSING: the institution has saved none. */
export function isLendingConfigurationMissing(error: unknown): boolean {
  return (
    error instanceof LendingConfigRequestError && error.code === 'LENDING_CONFIGURATION_MISSING'
  );
}

/** 503 LENDING_CONFIGURATION_INCOMPLETE: the saved one lacks a field. */
export function isLendingConfigurationIncomplete(error: unknown): boolean {
  return (
    error instanceof LendingConfigRequestError &&
    error.code === 'LENDING_CONFIGURATION_INCOMPLETE'
  );
}

/** The response body, or a LendingConfigRequestError with the server's code. */
function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || result.data === undefined) {
    const status = result.response?.status ?? 0;
    const body = (result.error ?? null) as Partial<ErrorResponse> | null;
    const missing = body?.details?.missingFields;
    throw new LendingConfigRequestError(
      typeof body?.message === 'string' && body.message.length > 0
        ? body.message
        : `Request failed (HTTP ${status})`,
      status,
      typeof body?.error === 'string' ? body.error : undefined,
      Array.isArray(missing) ? missing.filter((f): f is string => typeof f === 'string') : [],
    );
  }
  return result.data;
}

export const lendingConfigApi = {
  get: async (): Promise<LendingConfiguration> =>
    unwrap(await lendingConfigClient.GET('/api/v1/lending-config')).config,

  getDefaults: async (): Promise<LendingConfiguration> =>
    unwrap(await lendingConfigClient.GET('/api/v1/lending-config/defaults')).config,

  update: async (body: LendingConfigurationUpdateRequest): Promise<LendingConfiguration> =>
    unwrap(await lendingConfigClient.PUT('/api/v1/lending-config', { body })).config,
};
